import { useMemo } from "react";
import { Database, Twitter, Brain } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { automationQueries } from "@/api/automations";
import InteractionLog from "../automate/InteractionLog";
import type { Persona } from "@/api/personas";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Capabilities } from "@/components/manage-personas/capabilities/Capabilities.tsx";
import {
  CAPABILITY_CARD_COLORS,
  CapabilityCard,
} from "@/components/manage-personas/capabilities/CapabilityCard.tsx";
import { AgentInfoTab } from "@/components/manage-personas/AgentInfoTab.tsx";
import { ContentTabContent } from "@/components/manage-personas/ContentTabContent.tsx";

interface AgentEvolutionProps {
  personaData: Persona;
  docData: any;
  className?: string;
}

type TabType =
  | "info"
  | "summary"
  | "content"
  | "features"
  | "capabilities"
  | "autonomous"
  | "community"
  | "interactions";

type Tab = {
  id: TabType;
  label: string;
};

const tabs: Tab[] = [
  {
    id: "info",
    label: "Info",
  },
  {
    id: "summary",
    label: "Summary",
  },
  {
    id: "content",
    label: "Content",
  },
  {
    id: "features",
    label: "Features",
  },
  {
    id: "capabilities",
    label: "Capabilities",
  },
  {
    id: "interactions",
    label: "Interactions",
  },
];

const StatusBadge = ({
  isEnabled,
  label,
}: {
  isEnabled: boolean;
  label?: string;
}) => (
  <span
    className={cn(
      "text-sm px-3 py-1 rounded-md border transition-all duration-200",
      isEnabled
        ? "bg-[#DFFE00]/10 text-primary border-primary/20"
        : "bg-white/5 text-white/40 border-white/10",
    )}
  >
    {label || (isEnabled ? "Active" : "Inactive")}
  </span>
);

const FeatureCard = ({
  icon: Icon,
  title,
  status,
  description,
}: {
  icon: any;
  title: string;
  status: boolean;
  description?: string;
}) => (
  <div className="flex flex-col gap-3 p-4 rounded-2xl bg-[#1D1D1D] backdrop-blur-sm hover:border-primary/20 transition-all duration-200">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#DFFE00]/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <span className="text-sm font-medium text-white">{title}</span>
      </div>
      <StatusBadge isEnabled={status} />
    </div>
    {description && <p className="text-sm text-white/60">{description}</p>}
  </div>
);

export function AgentEvolution({ personaData, docData }: AgentEvolutionProps) {
  // Get automations data
  const { data: agentAutomations = [] } = useQuery({
    ...automationQueries.automations(personaData.persona_id),
    enabled: !!personaData.persona_id,
  });

  const enabledAutomations = useMemo(() => {
    return agentAutomations.filter((automation) => automation.enabled);
  }, [agentAutomations]);

  const getDaysOld = () => {
    if (!personaData.created_at) return "Age unknown";
    const createdDate = new Date(personaData.created_at);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - createdDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days old`;
  };

  return (
    <>
      <Tabs defaultValue="info">
        <ScrollArea>
          <TabsList className="mb-4 gap-5 bg-darkGray">
            {tabs.map((tab) => (
              <TabsTrigger
                className="data-[state=active]:font-bold data-[state=active]:bg-darkGray data-[state=active]:border-b-[2px] align-middle"
                value={tab.id}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <TabsContent value="info">
          <AgentInfoTab persona={personaData} />
        </TabsContent>
        <TabsContent className="max-h-[400px] overflow-y-auto" value="summary">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
              icon={Brain}
              title="Agent Online"
              status={true}
              description={`Agent is ${getDaysOld()}`}
            />
            <FeatureCard
              icon={Database}
              title="Knowledge Base"
              status={docData.last_built}
              description="Custom training data and memory"
            />
            <FeatureCard
              icon={Database}
              title="Live Data Integration"
              status={docData.live_data}
              description="Real-time data insights"
            />
            <FeatureCard
              icon={Twitter}
              title="Social Presence"
              status={!!personaData.twitter}
              description="Twitter integration enabled"
            />
          </div>
        </TabsContent>
        <TabsContent value="content">
          <ContentTabContent persona={personaData} />
        </TabsContent>

        <TabsContent value="features">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <CapabilityCard
              title="Live Chat"
              status={true}
              description="Interactive AI conversations"
              style={{
                // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
                "--color": CAPABILITY_CARD_COLORS[0],
              }}
            />
            {personaData.avatar_id ? (
              <CapabilityCard
                title="Image Creation"
                description="Digital image generation"
                status={true}
                style={{
                  // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
                  "--color": CAPABILITY_CARD_COLORS[1],
                }}
              />
            ) : null}
            {personaData.web3?.contractAddress ? (
              <CapabilityCard
                title="Agent Token"
                status={true}
                description="Web3 token enabled"
                style={{
                  // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
                  "--color": CAPABILITY_CARD_COLORS[2],
                }}
              />
            ) : null}
            {personaData.twitter !== "" ? (
              <CapabilityCard
                title="Twitter Posting"
                status={true}
                description="Automated social presence"
                style={{
                  // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
                  "--color": CAPABILITY_CARD_COLORS[3],
                }}
              />
            ) : null}
            {personaData.web3?.pool ? (
              <CapabilityCard
                title="Token Liquidity Pool"
                status={true}
                description="Active liquidity Pool"
                style={{
                  // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
                  "--color": CAPABILITY_CARD_COLORS[4],
                }}
              />
            ) : null}
            {enabledAutomations.some(
              (product) =>
                product.product_sku ===
                  "twitter-planning-automated-schedule-creation" &&
                product.enabled,
            ) ? (
              <CapabilityCard
                title="AI Led Content Strategy"
                status={true}
                description="Smart content automation"
                style={{
                  // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
                  "--color": CAPABILITY_CARD_COLORS[5],
                }}
              />
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="capabilities">
          <Capabilities persona={personaData} />
        </TabsContent>
        <TabsContent
          className="max-h-[400px] overflow-y-auto"
          value="interactions"
        >
          <InteractionLog agentId={personaData.persona_id} />
        </TabsContent>
      </Tabs>
    </>
  );
}
